import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  fetchIntegrationSyncStatus,
  queueBiometricReconcile,
  queueShopifyPullOrders,
  type IntegrationSyncStatus,
} from "../../api/integrations-sync";
import { ApiClientError } from "../../api/types";
import { useAuth } from "../../context/AuthContext";
import {
  FinanceAdminRoles,
  RoleCode,
  hasRole,
  type RoleCodeValue,
} from "../../lib/rbac";

function healthDotClass(health: IntegrationSyncStatus["zkteco"]["health"]): string {
  switch (health) {
    case "online":
      return "bg-emerald-500";
    case "degraded":
      return "bg-amber-400";
    case "offline":
      return "bg-red-500";
    default:
      return "bg-slate-300";
  }
}

export function IntegrationSyncHeader() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const roles = (profile?.roles ?? []) as RoleCodeValue[];
  const canSync = hasRole(roles, ...FinanceAdminRoles);
  const canSeeHealth = canSync || hasRole(roles, RoleCode.HR_MANAGER);

  const [status, setStatus] = useState<IntegrationSyncStatus | null>(null);
  const [busy, setBusy] = useState<"shopify" | "biometric" | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    if (!canSeeHealth) return;
    try {
      const data = await fetchIntegrationSyncStatus();
      setStatus(data);
    } catch {
      setStatus(null);
    }
  }, [canSeeHealth]);

  useEffect(() => {
    void loadStatus();
    const id = window.setInterval(() => void loadStatus(), 60_000);
    return () => window.clearInterval(id);
  }, [loadStatus]);

  if (!canSeeHealth) return null;

  async function runShopifyPull() {
    setBusy("shopify");
    setToast(null);
    try {
      await queueShopifyPullOrders();
      setToast(t("integrations.sync_shopify_queued"));
      void loadStatus();
    } catch (err) {
      setToast(
        err instanceof ApiClientError
          ? err.message
          : t("integrations.sync_failed"),
      );
    } finally {
      setBusy(null);
    }
  }

  async function runBiometricReconcile() {
    setBusy("biometric");
    setToast(null);
    try {
      await queueBiometricReconcile();
      setToast(t("integrations.sync_zkteco_queued"));
    } catch (err) {
      setToast(
        err instanceof ApiClientError
          ? err.message
          : t("integrations.sync_failed"),
      );
    } finally {
      setBusy(null);
    }
  }

  const zkHealth = status?.zkteco.health ?? "none";
  const shopReady = status?.shopify.configured ?? false;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Link
        to="/hr/attendance"
        className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[10px] font-medium text-slate-700 hover:bg-slate-50"
        title={t(`integrations.zkteco_health_${zkHealth}`)}
      >
        <span
          className={`h-2 w-2 shrink-0 rounded-full ${healthDotClass(zkHealth)}`}
          aria-hidden
        />
        <span className="hidden sm:inline">{t("integrations.zkteco_label")}</span>
        {status && status.zkteco.activeDeviceCount > 0 && (
          <span className="text-slate-400" dir="ltr">
            ({status.zkteco.activeDeviceCount})
          </span>
        )}
      </Link>

      {canSync && (
        <>
          <button
            type="button"
            disabled={!shopReady || busy === "shopify"}
            onClick={() => void runShopifyPull()}
            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-[10px] font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            title={
              shopReady
                ? t("integrations.sync_shopify_pull")
                : t("integrations.shopify_not_configured")
            }
          >
            {busy === "shopify" ? "…" : t("integrations.sync_shopify_short")}
          </button>
          <button
            type="button"
            disabled={busy === "biometric"}
            onClick={() => void runBiometricReconcile()}
            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-[10px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            title={t("integrations.sync_zkteco_reconcile")}
          >
            {busy === "biometric" ? "…" : t("integrations.sync_zkteco_short")}
          </button>
        </>
      )}

      {toast && (
        <span className="max-w-[140px] truncate text-[10px] text-emerald-700" role="status">
          {toast}
        </span>
      )}
    </div>
  );
}
